import { Injectable } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { Clipboard } from '@angular/cdk/clipboard'
import { Observable, of } from 'rxjs'
import { catchError, map, tap } from 'rxjs/operators'
import { CallerService } from './caller.service'
import { AnalyticsService } from './analytics.service'
import { LoginSignupPromptComponent } from '../dialogs/login-signup-prompt/login-signup-prompt.component'
import { accommodationSaveChanged, loadSavedAccommodations, newMessage } from '../ngrx-store/actions/corp-member.actions'
import { selectSavedAccommodationIds } from '../ngrx-store/selectors/corp.selectors'
import { AccommodationType } from '../ngrx-store/app.state'
import { chatIdentityOf } from '../utils/corp-member.util'
import { saleLocation } from '../utils/sale.util'
import { accommodationTypeLabel, accommodationUrl } from '../utils/accommodation.util'

/**
 * What you can do with a house - save it, chat its poster, share it - for the house card and the house page
 * (/a/:id). Like SaleActionsService for sales.
 */
@Injectable({ providedIn: 'root' })
export class AccommodationActionsService {
  constructor(
    private callerService: CallerService,
    private dialog: MatDialog,
    private clipboard: Clipboard,
    private analyticsService: AnalyticsService,
  ) {}

  /** The saved house ids - loaded once per login, however many cards ask (see the effect). */
  savedIds(): Observable<Set<number>> {
    this.callerService.store.dispatch(loadSavedAccommodations())
    return this.callerService.store.select(selectSavedAccommodationIds)
  }

  /** Saves a house, or removes it from saved; emits whether it's saved now. Logged out, it asks them to log in. */
  toggleSave(accommodationId: number, saved: boolean): Observable<boolean> {
    if (!this.callerService.isLoggedIn) {
      this.openLoginPrompt()
      return of(saved)
    }

    const request$ = saved
      ? this.callerService.unBookmarkAccommodation(accommodationId)
      : this.callerService.bookmarkAccommodation(accommodationId)
    return request$.pipe(
      map(() => !saved),
      tap((nowSaved) => {
        this.callerService.store.dispatch(accommodationSaveChanged({ id: accommodationId, saved: nowSaved }))
        this.callerService.showNotification(nowSaved ? 'Saved. Find it on your dashboard.' : 'Removed from saved.')
      }),
      catchError((err) => {
        this.callerService.showNotification(
          err?.error?.message ?? (saved ? 'Couldn\'t remove it from saved. Please try again.' : 'Couldn\'t save it. Please try again.'),
        )
        return of(saved)
      }),
    )
  }

  /**
   * Opens a chat with the poster, with a first message about the place typed in for them (the link tells the
   * poster which one). Logged out, it asks them to log in.
   */
  chat(accommodation: AccommodationType): void {
    if (!this.callerService.isLoggedIn) {
      this.openLoginPrompt()
      return
    }

    const recipientId = chatIdentityOf(accommodation.CorpMember)
    const initiatorId = chatIdentityOf(this.callerService.corpMember)
    if (!recipientId || !initiatorId) {
      this.callerService.showNotification('Sorry, we couldn\'t find the poster.')
      return
    }

    const posterName = accommodation.CorpMember?.first_name ?? accommodation.CorpMember?.nickname
    const messagePayload: any = {
      recipient_id: recipientId,
      recipient_state_code: accommodation.CorpMember?.state_code ?? '',
      recipient_name: posterName,
      initiator_state_code: this.callerService.corpMember.state_code ?? '',
      initiator_name: this.callerService.corpMember.first_name,
      initiator_id: initiatorId,
      // Listings don't include the poster's state code any more (it's a login identifier), so
      // the chat starts with their name; the chat list fills the state code in from /chat.
      chat_display_name: posterName,
      chat_display_state_code: accommodation.CorpMember?.state_code,
      // A first message about the place, with its link, typed in for them.
      draft: `Hi, is your ${accommodation.accommodation_type?.trim().toLowerCase() || 'place'} still available? ${accommodationUrl(accommodation)}`,
      listing: accommodation.slug_id ? { listing_type: 'accommodation', listing_slug: accommodation.slug_id } : undefined,
    }
    // Only add source if accommodation is paid (rent > 0) from an active rent
    if (accommodation.rent > 0 && ['active_rent', 'new_rent'].includes(accommodation.status ?? '')) {
      messagePayload.source = 'accommodation'
    }

    this.analyticsService.chatStarted('accommodation')
    this.callerService.store.dispatch(newMessage(messagePayload))
  }

  /** The phone's share sheet (WhatsApp and the rest) where there is one, else the link is copied. */
  async share(accommodation: AccommodationType): Promise<void> {
    const url = accommodationUrl(accommodation)
    const what = accommodationTypeLabel(accommodation.accommodation_type)
    const where = saleLocation(accommodation.CorpMember?._location)
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: what, text: `${what}${where ? ` in ${where}` : ''} on corpers.ng`, url })
      } catch (err) {
        // AbortError: they closed the sheet. Anything else, fall back to copying.
        if ((err as DOMException)?.name !== 'AbortError') this.copyLink(url)
      }
      return
    }
    this.copyLink(url)
  }

  copyPhoneNumber(phoneNumber: string): void {
    const copied = this.clipboard.copy(phoneNumber)
    this.callerService.showNotification(copied ? 'Copied phone number.' : 'Couldn\'t copy it. Please try again.')
  }

  private copyLink(url: string): void {
    const copied = this.clipboard.copy(url)
    this.callerService.showNotification(copied ? 'Link copied - paste it anywhere to share.' : 'Couldn\'t copy the link. Please try again.')
  }

  private openLoginPrompt(): void {
    this.dialog.open(LoginSignupPromptComponent, { maxWidth: '360px', data: {} })
  }
}
